import {
	Component,
	Input,
	OnChanges,
	SimpleChanges,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { firstValueFrom } from 'rxjs';
import { MatFormField, MatPrefix, MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import {
	NgIf,
	NgFor,
	NgClass,
	NgTemplateOutlet,
	AsyncPipe,
	DatePipe,
} from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { HighlightSegmentsPipe } from './highlight-segments.pipe';

type TimelineActionKind = 'added' | 'updated' | 'deleted' | 'other';

interface TimelineFieldChange {
	label: string;
	oldValue: string;
	newValue: string;
}

interface TimelineRow {
	id: string;
	auditedTime: string;
	dayKey: string;
	action: string;
	actionKind: TimelineActionKind;
	doneBy: string;
	source: string;
	automation: string;
	recordModule: string;
	recordName: string;
	changes: TimelineFieldChange[];
	isFunction: boolean;
	searchText: string;
}

@Component({
	selector: 'app-timeline',
	templateUrl: './timeline.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./timeline.component.scss'],
	imports: [
		MatFormField,
		MatIcon,
		MatPrefix,
		MatInput,
		NgIf,
		MatProgressSpinner,
		MatSlideToggle,
		MatTooltip,
		MatButton,
		MatIconButton,
		NgFor,
		NgClass,
		NgTemplateOutlet,
		MatPaginator,
		AsyncPipe,
		DatePipe,
		HighlightSegmentsPipe,
	],
})
export class TimelineComponent implements OnChanges {
	@Input() applicationId: string;

	@ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
		this.dataSource.paginator = paginator;
	}

	protected readonly actionIcons: Record<TimelineActionKind, string> = {
		added: 'add',
		updated: 'edit',
		deleted: 'delete',
		other: 'history',
	};
	protected dataSource = new MatTableDataSource<TimelineRow>([]);
	// Current page of filtered rows, rendered by the feed
	protected rows$ = this.dataSource.connect();
	protected sortDesc = true;
	protected searchTerm = '';
	// function_executed and automated (workflow rule / function) entries are noise for most users; hidden unless toggled on
	protected showFunctions = false;
	private readonly hiddenAutomationTypes = ['workflow_rule', 'functions'];
	protected isLoading = false;
	protected isLoadingMore = false;
	protected hasMore = false;
	protected isSearchingAll = false;
	protected errorMessage = '';
	private readonly perPage = 200;
	private currentLoadId = 0;
	private nextPage = 1;
	private nextPageToken: string | undefined;
	private inFlightBatch: Promise<void> | null = null;
	private searchLoadId: number | null = null;
	private stopSearchRequested = false;

	constructor(private readonly applicationService: ApplicationService) {
		// Filter string is only a change trigger (see updateFilter); real state is on the component
		this.dataSource.filterPredicate = (row) =>
			(this.showFunctions || !row.isFunction) &&
			(!this.searchTerm || row.searchText.includes(this.searchTerm));
		this.updateFilter();
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes.applicationId?.currentValue) {
			this.loadTimeline();
		}
	}

	// Resets and loads the first batch of 200; later batches load on demand (paging or searching)
	protected async loadTimeline() {
		const loadId = ++this.currentLoadId;
		this.inFlightBatch = null;
		this.dataSource.data = [];
		this.dataSource.paginator?.firstPage();
		this.nextPage = 1;
		this.nextPageToken = undefined;
		this.hasMore = true;
		this.isLoadingMore = false;
		this.isLoading = true;
		await this.loadNextBatch();
		if (loadId !== this.currentLoadId) {
			return;
		}
		// A search typed before/while refreshing still needs the full timeline
		if (this.searchTerm) {
			this.loadAllForSearch();
		} else {
			this.fillPastCurrentPage();
		}
	}

	// Fetch more once the user pages onto the last loaded rows (e.g. rows 176-200)
	protected onPageChange(event: PageEvent) {
		this.fillPastCurrentPage();
	}

	// The paginator only counts visible (filtered) rows, so when hidden function entries leave too few
	// to go past the current page, Next is disabled even though the server has more. Keep fetching
	// batches until there is a next page or nothing is left. Searching has its own loop (loadAllForSearch).
	private async fillPastCurrentPage() {
		const loadId = this.currentLoadId;
		while (
			this.hasMore &&
			!this.searchTerm &&
			!this.errorMessage &&
			loadId === this.currentLoadId
		) {
			const paginator = this.dataSource.paginator;
			const pageEnd = paginator
				? (paginator.pageIndex + 1) * paginator.pageSize
				: 0;
			if (this.dataSource.filteredData.length > pageEnd) {
				return;
			}
			await this.loadNextBatch();
		}
	}

	protected applyFilter(event: Event) {
		this.searchTerm = (event.target as HTMLInputElement).value
			.trim()
			.toLowerCase();
		this.updateFilter();
		if (this.searchTerm) {
			this.loadAllForSearch();
		} else {
			this.fillPastCurrentPage();
		}
	}

	// Search only sees loaded rows, so keep fetching batches while a search is active.
	// Stops when everything is loaded, the search is cleared, Stop is pressed, a batch fails, or a new load starts.
	private async loadAllForSearch() {
		const loadId = this.currentLoadId;
		// Typing again resumes a stopped search
		this.stopSearchRequested = false;
		if (this.searchLoadId === loadId) {
			this.isSearchingAll = true;
			return;
		}
		this.searchLoadId = loadId;
		this.isSearchingAll = true;
		try {
			while (
				this.hasMore &&
				this.searchTerm &&
				!this.stopSearchRequested &&
				loadId === this.currentLoadId
			) {
				await this.loadNextBatch();
				if (this.errorMessage) {
					break;
				}
			}
		} finally {
			if (this.searchLoadId === loadId) {
				this.searchLoadId = null;
				this.isSearchingAll = false;
			}
		}
	}

	// Picks up from the next unfetched batch (nextPage / nextPageToken); loaded rows are kept
	protected continueSearch() {
		this.loadAllForSearch();
	}

	// The batch already in flight still finishes; no further batches are fetched
	protected stopSearch() {
		this.stopSearchRequested = true;
		this.isSearchingAll = false;
	}

	protected loadMore() {
		this.loadNextBatch();
	}

	protected toggleFunctions(show: boolean) {
		this.showFunctions = show;
		this.updateFilter();
		this.fillPastCurrentPage();
	}

	// MatTableDataSource only re-filters when the filter string changes, so encode both inputs in it
	private updateFilter() {
		this.dataSource.filter = `${this.showFunctions ? 'fn' : 'no-fn'}|${this.searchTerm}`;
		this.dataSource.paginator?.firstPage();
	}

	protected toggleSort() {
		this.sortDesc = !this.sortDesc;
		this.dataSource.data = this.sortRows(this.dataSource.data);
		this.dataSource.paginator?.firstPage();
	}

	protected trackRow(index: number, row: TimelineRow) {
		return row.id;
	}

	// Matches against the text as displayed (empty values show as 'empty')
	protected changeMatches(change: TimelineFieldChange): boolean {
		if (!this.searchTerm) {
			return false;
		}
		const display = (value: string) => (value === '—' ? 'empty' : value);
		return [
			change.label,
			display(change.oldValue),
			display(change.newValue),
		].some((text) => text.toLowerCase().includes(this.searchTerm));
	}

	// Only dim non-matching changes when the row matched on a change (not on user / action / automation)
	protected hasMatchingChange(row: TimelineRow): boolean {
		return row.changes.some((change) => this.changeMatches(change));
	}

	// Concurrent callers (paging, Load more, search loop) share the same in-flight request
	private loadNextBatch(): Promise<void> {
		if (this.inFlightBatch) {
			return this.inFlightBatch;
		}
		if (!this.hasMore) {
			return Promise.resolve();
		}
		const batch = this.fetchBatch().finally(() => {
			if (this.inFlightBatch === batch) {
				this.inFlightBatch = null;
			}
		});
		this.inFlightBatch = batch;
		return batch;
	}

	private async fetchBatch() {
		const loadId = this.currentLoadId;
		const page = this.nextPage;
		const pageToken = this.nextPageToken;
		this.isLoadingMore = !this.isLoading;
		this.errorMessage = '';
		try {
			const res = await firstValueFrom(
				this.applicationService.getApplicationTimeline(
					this.applicationId,
					page,
					this.perPage,
					pageToken
				)
			);
			// A newer load (refresh / application change) has started, drop this one
			if (loadId !== this.currentLoadId) {
				return;
			}
			const entries: any[] = res?.__timeline ?? [];
			this.dataSource.data = this.sortRows([
				...this.dataSource.data,
				...entries.map((entry) => this.toRow(entry)),
			]);
			this.hasMore = !!res?.info?.more_records && entries.length > 0;
			this.nextPageToken = res?.info?.next_page_token;
			this.nextPage = page + 1;
		} catch (err) {
			if (loadId !== this.currentLoadId) {
				return;
			}
			const prefix = this.dataSource.data.length
				? `Failed to load more timeline entries (batch ${page})`
				: 'Failed to load timeline';
			const detail = this.extractErrorDetail(err);
			this.errorMessage = detail ? `${prefix}: ${detail}` : prefix;
			console.log(err, 'Timeline load failed!', { page, pageToken });
		} finally {
			if (loadId === this.currentLoadId) {
				this.isLoading = false;
				this.isLoadingMore = false;
			}
		}
	}

	private toRow(entry: any): TimelineRow {
		const changes: TimelineFieldChange[] = (entry.field_history ?? []).map(
			(field: any) => ({
				label: field.field_label ?? field.api_name,
				oldValue: this.formatValue(field._value?.old),
				newValue: this.formatValue(field._value?.new),
			})
		);
		const rule = entry.automation_details?.rule?.name;
		const automationType = entry.automation_details?.type;
		const auditedTime = entry.audited_time ?? '';
		const action = entry.action ?? '';
		const doneBy = entry.done_by?.name ?? '';
		const row: TimelineRow = {
			id: entry.id ?? `${auditedTime}-${doneBy}-${action}`,
			auditedTime,
			dayKey: auditedTime.substring(0, 10),
			action,
			actionKind: this.toActionKind(action),
			doneBy,
			source: entry.source ?? '',
			automation: [rule, automationType].filter(Boolean).join(' · '),
			recordModule: entry.record?.module?.api_name ?? '',
			recordName: entry.record?.name ?? '',
			changes,
			isFunction:
				action.toLowerCase() === 'function_executed' ||
				this.hiddenAutomationTypes.includes(automationType),
			searchText: '',
		};
		row.searchText = [
			row.auditedTime,
			row.action,
			row.doneBy,
			row.source,
			row.automation,
			row.recordModule,
			row.recordName,
			...changes.map((c) => `${c.label} ${c.oldValue} ${c.newValue}`),
		]
			.join(' ')
			.toLowerCase();
		return row;
	}

	private sortRows(rows: TimelineRow[]): TimelineRow[] {
		const direction = this.sortDesc ? -1 : 1;
		return [...rows].sort(
			(a, b) =>
				direction *
				(new Date(a.auditedTime).getTime() - new Date(b.auditedTime).getTime())
		);
	}

	private toActionKind(action: string): TimelineActionKind {
		const normalized = action.toLowerCase();
		if (normalized.includes('add') || normalized.includes('creat')) {
			return 'added';
		}
		if (normalized.includes('updat') || normalized.includes('edit')) {
			return 'updated';
		}
		if (normalized.includes('delet') || normalized.includes('remov')) {
			return 'deleted';
		}
		return 'other';
	}

	// Backend forwards Zoho's error body (often a JSON string) in `message`
	private extractErrorDetail(err: any): string {
		const message = err?.error?.message;
		if (!message) {
			return '';
		}
		try {
			const parsed =
				typeof message === 'string' ? JSON.parse(message) : message;
			return (
				[parsed.code, parsed.message].filter(Boolean).join(' - ') ||
				JSON.stringify(parsed)
			);
		} catch {
			return String(message);
		}
	}

	private formatValue(value: any): string {
		if (value === null || value === undefined || value === '') {
			return '—';
		}
		if (typeof value === 'object') {
			return value.name ?? JSON.stringify(value);
		}
		return String(value);
	}
}
